// Design tokens based on privacv design system - light mode only
const tokens = {
  // Color palette using HSL values from design system
  colors: {
    primary: {
      main: '#0078FF', // hsl(210 100% 50%)
      light: '#5dade2', // Lighter variant
      dark: '#2980b9', // Darker variant
      contrast: '#ffffff',
    },
    secondary: {
      main: '#6c7ae0', // Complementary to primary
      light: '#8e99e6',
      dark: '#5563d8',
      contrast: '#ffffff',
    },
    // Neutral palette based on HSL values
    neutral: {
      50: 'hsl(210 40% 98%)', // Background
      100: 'hsl(210 40% 96.1%)', // Muted
      200: 'hsl(214.3 31.8% 91.4%)', // Border
      300: 'hsl(213 27% 84%)',
      400: 'hsl(215 20% 65%)',
      500: 'hsl(215 16% 47%)',
      600: 'hsl(215 19% 35%)',
      700: 'hsl(215 25% 27%)',
      800: 'hsl(217 33% 17%)',
      900: 'hsl(222.2 84% 4.9%)', // Foreground
    },
    success: '#10b981', // Emerald from Tailwind
    warning: '#f59e0b', // Amber from Tailwind
    error: '#ef4444', // Red from Tailwind
    info: '#3b82f6', // Blue from Tailwind
    background: {
      default: '#ffffff', // Light background
      paper: '#ffffff',
      alt: '#fafbfc', // Alternative background for contrast
      gradient: 'radial-gradient(circle at center, rgb(249, 250, 251), rgb(240, 245, 255))', // gradient-subtle
    },
    borders: {
      default: 'hsl(214.3 31.8% 91.4%)',
      light: 'hsl(214.3 31.8% 91.4%)',
    },
    shadows: {
      default: 'rgba(0, 0, 0, 0.05)',
    },
    text: {
      primary: 'hsl(222.2 84% 4.9%)', // Main text color
      secondary: 'hsl(215 16% 47%)', // Secondary text color
    },
    // Transparency values for UI elements
    alpha: {
      white: {
        50: 'rgba(255, 255, 255, 0.03)',
        100: 'rgba(255, 255, 255, 0.05)',
        200: 'rgba(255, 255, 255, 0.1)',
        300: 'rgba(255, 255, 255, 0.2)',
        800: 'rgba(255, 255, 255, 0.8)',
        900: 'rgba(255, 255, 255, 0.9)'
      },
      black: {
        50: 'rgba(0, 0, 0, 0.05)',
        100: 'rgba(0, 0, 0, 0.1)',
        800: 'rgba(255, 255, 255, 0.8)',
        900: 'rgba(255, 255, 255, 0.95)'
      }
    },
  },

  // Typography
  typography: {
    fontFamily: {
      primary: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      mono: '"JetBrains Mono", monospace',
    },
    fontSizes: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem' // 30px
    },
    fontWeights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeights: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
    }
  },

  // Spacing
  spacing: {
    0: '0',
    px: '1px',
    0.5: '0.125rem', // 2px
    1: '0.25rem',    // 4px
    1.5: '0.375rem', // 6px
    2: '0.5rem',     // 8px
    3: '0.75rem',    // 12px
    4: '1rem',       // 16px
    5: '1.25rem',    // 20px
    6: '1.5rem',     // 24px
    8: '2rem',       // 32px
    10: '2.5rem',    // 40px
    12: '3rem',      // 48px
    16: '4rem',      // 64px
    20: '5rem',      // 80px
    24: '6rem',      // 96px
    input: {
      height: '2.5rem',
      padding: '0.75rem',
      labelOffset: '0.3rem',
      marginBottom: '1rem',
      gap: '0.5rem',
      labelPadding: {
        left: '0.25rem',
      }
    },
    container: {
      xs: '1rem',
      sm: '1.5rem',
      md: '2rem',
      lg: '3rem',
    },
  },

  // Border radius
  radius: {
    none: '0',
    sm: '0.25rem',    // 4px
    base: '0.5rem',   // 8px - Standard radius
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    '2xl': '1rem',    // 16px
    full: '9999px',
  },

  // Shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    soft: '0 10px 25px -5px rgba(0, 0, 0, 0.05)',
    elegant: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
    highlight: '0 0 15px rgba(52, 152, 219, 0.3)',
  },

  // Z-index
  zIndex: {
    behind: -1,
    base: 0,
    above: 1,
    dropdown: 1000,
    modal: 1300,
    toast: 1400,
    tooltip: 1500,
  },

  // Transitions
  transitions: {
    standard: '0.2s ease',
    smooth: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: '0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    durations: {
      quick: '0.2s',
      standard: '0.3s',
      slow: '0.5s',
    }
  },

  // Component-specific tokens
  components: {
    button: {
      primary: {
        background: '#0078FF', // Primary color
        hoverBackground: '#0068E0', // Slightly darker on hover
        text: '#ffffff',
        padding: '0.5rem 1rem',
      },
      secondary: {
        background: '#ffffff',
        hoverBackground: '#f8fafc',
        text: '#0078FF',
        border: '1px solid #e2e8f0',
        padding: '0.5rem 1rem',
      },
      text: {
        color: 'hsl(215 16% 47%)', // neutral 500
        hoverColor: '#0078FF', // primary.main
      },
      danger: {
        background: '#ef4444',
        hoverBackground: '#dc2626',
        text: '#ffffff',
        padding: '0.5rem 1rem',
      },
    },
    header: {
      height: '64px',
      padding: {
        mobile: '0.75rem 1rem',
        desktop: '1rem 2rem',
      },
      logo: {
        padding: {
          mobile: '0',
          desktop: '0 1rem', // Consistent logo padding
        },
        flexShrink: 0, // Prevent logo from shrinking
      },
      navigation: {
        gap: {
          mobile: '0.5rem',
          desktop: '1.5rem',
          large: '2rem',
        },
        padding: {
          mobile: '0.5rem 0.75rem',
          desktop: '0.75rem 1rem',
        },
      },
      buttons: {
        minWidth: '120px',
        padding: {
          mobile: '0.5rem 1rem',
          desktop: '0.75rem 2rem',
        },
        fontSize: '0.875rem',
      },
      layout: {
        justifyContent: 'space-between',
        alignItems: 'center',
        maxHeight: '64px', // Enforce consistent height
        minHeight: '64px', // Enforce consistent height
      },
    },
    card: {
      padding: '1.5rem',
      borderRadius: '0.5rem',
    },
    preview: {
      padding: '20px',
    },
  },
};

export default tokens; 